import { LoadingService } from 'src/app/core/services/loading.service';
import { UserState } from 'src/app/core/models/user-state';
import { RoleService } from 'src/app/core/services/role.service';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { CurrentUser, SubuserCreateRequestDto, User } from 'src/app/core/models/user';
import { UserPermission } from 'src/app/core/models/user-permission';
import { UserService } from 'src/app/core/services/open-api/user.service';
import { TranslateService } from '@ngx-translate/core';
import { License, LicenseState, TrialState } from 'src/app/core/services/utils/license-state';
import { EditableRow, Table, TableRowSelectEvent } from 'primeng/table';
import { Router } from '@angular/router';
import { catchError, finalize, forkJoin, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { CrmWebhookEvent, CrmWebhookService } from 'src/app/core/services/crm-webhook.service';
import { SelectedItem } from 'src/app/core/models/selected-item.interface';
import { GroupForm } from '@/core/models/group-form';
import { GroupService } from 'src/app/core/services/open-api/group.service';
import { Account, UserForm, UserUpdateForm } from 'src/app/core/models/account';
import { DataUtils } from '@/core/utils/data-utils';
import { StdToasts, ToastService } from '@/core/services/toast.service';
import { CloneUtils } from '@/core/utils/clone-utils';
import { HttpErrorResponse } from '@angular/common/http';
import { UserUpdateErrorService } from '@/core/services/user-update-error.service';
import { AppConfirmation } from '@/core/types/angular-primeng-api';

type RoleCheck = '✓' | '';
type MemberNameField = 'firstName' | 'lastName';

type UserRoleInfo = {
    Title: string;
    TrackTime: RoleCheck;
    AccessOwnData: RoleCheck;
    AccessTeamData: RoleCheck;
    CreateTiles: RoleCheck;
    CreateReports: RoleCheck;
    ConfigureLayers: RoleCheck;
    ManageTeam: RoleCheck;
    ManagePayments: RoleCheck;
};

@Component({
    selector: 'app-members',
    templateUrl: './members.component.html',
    styleUrls: ['./members.component.scss'],
    providers: [EditableRow],
    standalone: false,
})
export class MembersComponent implements OnInit, OnDestroy {
    public UserState = UserState;
    stateOptions!: SelectedItem[];
    users: User[] = [];
    selectedUser?: User;
    currentUser?: CurrentUser;
    showAddEditDialog = false;
    showUserRolesInfo = false;
    newUser = false;
    user: User = new User();
    licenses?: number;
    displayLicenseCounter = false;
    displayEmptyPage = true;
    // newUser
    newUserState?: UserState;
    newUserRole?: UserPermission[];
    private originalUserRole?: UserPermission[];
    newUserEmail = '';
    newUserFirstname = '';
    newUserLastname = '';
    emailChanged: boolean = false;
    roles: SelectItem<UserPermission[]>[] = [];
    userStates: SelectItem[] = [];
    userRolesInfoTable: UserRoleInfo[] = [];
    isPro?: boolean; // kurz true setzen damit -message nicht beim laden aufleuchtet
    groups: GroupForm[] = [];
    userGroupsMap: Map<number, string[]> = new Map();
    selectedGroups: GroupForm[] = [];
    loading: boolean = false;
    selUserIsCurrUser: boolean = false;
    userStatusOptions = [
        { label: 'Active', value: UserState.Active },
        { label: 'Inactive', value: UserState.Inactive },
    ];
    selectedUserStatus = UserState.Active;
    usersOriginal: User[] = [];
    deleteDialogVisible: boolean = false;
    selectedUserToDelete: User | null = null;
    deleteForm: FormGroup;
    loadingCB: boolean = false;
    isSelectedUserInactive: boolean = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private deleteTimeout?: number | any;
    // private deleteTimeout?: number; <-- this would be correct, but it causes issues in WebStorm due to having the
    // frontend and backend open in the same project and WebStorm typescript thinking that the type is NodeJS.Timeout..
    @ViewChild('dt1') dt?: Table;
    private _search?: string;

    set search(text: string) {
        this.dt?.filterGlobal(text, 'contains');
        this._search = text;
    }

    get search(): string {
        return this._search ?? '';
    }

    @Output() groupsUpdated = new EventEmitter<GroupForm[]>();
    @Output() updateParent = new EventEmitter<void>();
    public minLicenceMessage = false;
    public activeUsers = 1;
    private hasTrial = false;
    private subscriptions: Subscription = new Subscription();

    constructor(
        private readonly crmWebhookService: CrmWebhookService,
        private readonly roleService: RoleService,
        private userService: UserService,
        private readonly translate: TranslateService,
        public readonly licenseState: LicenseState,
        private toastService: ToastService,
        public readonly loadingService: LoadingService,
        private router: Router,
        private groupService: GroupService,
        private readonly confirmationService: ConfirmationService,
        private fb: FormBuilder,
        private readonly userUpdateError: UserUpdateErrorService,
    ) {
        this.roles.push({
            label: 'Admin',
            value: this.roleService.adminRoles,
        });
        this.roles.push({
            label: 'Manager',
            value: this.roleService.managerRoles,
        });

        this.deleteForm = this.fb.group({
            deleteConfirmation: ['', [Validators.required, Validators.pattern(/^DELETE$/i)]],
        });

        this.translate
            .get(['regular_user', 'state_active', 'state_deleted', 'state_inactive'])
            .subscribe((translations) => {
                this.userRolesInfoTable = [
                    {
                        Title: 'Admin',
                        TrackTime: '✓',
                        AccessOwnData: '✓',
                        AccessTeamData: '✓',
                        CreateTiles: '✓',
                        CreateReports: '✓',
                        ConfigureLayers: '✓',
                        ManageTeam: '✓',
                        ManagePayments: '✓',
                    },
                    {
                        Title: 'Manager',
                        TrackTime: '✓',
                        AccessOwnData: '✓',
                        AccessTeamData: '✓',
                        CreateTiles: '✓',
                        CreateReports: '✓',
                        ConfigureLayers: '',
                        ManageTeam: '',
                        ManagePayments: '',
                    },
                    {
                        Title: translations.regular_user,
                        TrackTime: '✓',
                        AccessOwnData: '✓',
                        AccessTeamData: '',
                        CreateTiles: '✓',
                        CreateReports: '',
                        ConfigureLayers: '',
                        ManageTeam: '',
                        ManagePayments: '',
                    },
                ];

                this.userStates.push({
                    label: translations.state_active,
                    value: UserState.Active,
                });
                this.userStates.push({
                    label: translations.state_inactive,
                    value: UserState.Inactive,
                });

                this.roles.push({
                    label: translations.regular_user,
                    value: this.roleService.userRoles,
                });
            });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        this.loadingService.setLoading(true);

        forkJoin({
            allGroups: this.groupService.getAllGroups(),
            account: this.userService.loadAccount(),
        }).subscribe(({ allGroups, account }: { allGroups: GroupForm[]; account: Account }) => {
            this.groups = allGroups;

            this.usersOriginal = account.getUsers();
            this.users = account.getActiveUsers();

            if (this.usersOriginal.length > 1) {
                this.displayEmptyPage = false;
            }
            // Update the userGroupsMap
            this.users.forEach((user: User) => {
                this.userGroupsMap.set(user.id, this.setUserGroup(user));
            });

            this.loadingService.setLoading(false);
        });

        this.subscriptions.add(
            this.userService.currentUser$.subscribe((user) => {
                this.currentUser = user;
                this.isPro = user.isPro();
            }),
        );
        this.userService.loadCurrentUser();

        this.subscriptions.add(
            this.licenseState.getLicense().subscribe((license: License) => {
                this.activeUsers = license.activeUsers;
                this.licenses = license.licenses;
                this.hasTrial = license.feature.trial.trialState == TrialState.ACTIVE;

                if (this.activeUsers > this.licenses && this.licenses >= 1) {
                    this.minLicenceMessage = true;
                }

                if (!this.hasTrial) {
                    this.displayLicenseCounter = true;
                }
            }),
        );
    }

    get deleteConfirmation(): AbstractControl | null {
        return this.deleteForm.get('deleteConfirmation');
    }

    clearDeleteConfirmation(): void {
        this.deleteConfirmation?.setValue('');
        this.deleteForm.reset();
    }

    startDeleteLoading(): void {
        this.loadingCB = true;

        setTimeout(() => {
            document.querySelector('.delayed-loading-btn')?.classList.add('loading');
        }, 10);

        this.deleteTimeout = setTimeout(() => {
            this.deleteUser();
            this.loadingCB = false;
            document.querySelector('.delayed-loading-btn')?.classList.remove('loading');
        }, 3000);
    }

    cancelDelete(): void {
        this.loadingCB = false;
        clearTimeout(this.deleteTimeout);
        document.querySelector('.delayed-loading-btn')?.classList.remove('loading');
    }

    closeDeleteDialog(): void {
        this.cancelDelete();
        this.loading = false;
        this.deleteDialogVisible = false;
    }

    confirmUserStateChange(newState: UserState): void {
        const isActivating = newState === UserState.Active;

        const confirmation: AppConfirmation = {
            message: this.translate
                .instant(isActivating ? 'user_activate_confirm' : 'user_deactivate_confirm')
                .replace('{{userName}}', this.selectedUser?.firstName + ' ' + this.selectedUser?.lastName),
            header: this.translate
                .instant(isActivating ? 'user_activate_confirm_header' : 'user_deactivate_confirm_header')
                .replace('{{userName}}', this.selectedUser?.firstName + ' ' + this.selectedUser?.lastName),
            icon: 'pi pi-info-circle',
            // 'warning' is an AppConfirmation field rendered by the shared dialog in app.component.html, not a PrimeNG option.
            // Warn when changing the member's state will discard unsaved edits.
            warning: this.hasUnsavedChanges() ? this.translate.instant('unsaved_changes_discarded') : undefined,
            acceptLabel: isActivating
                ? this.translate.instant('yes_activate')
                : this.translate.instant('yes_deactivate'),
            rejectLabel: this.translate.instant('button_cancel'),
            acceptIcon: 'none',
            rejectIcon: 'none',
            acceptButtonStyleClass: isActivating ? 'p-button-primary' : 'p-button-primary',
            rejectButtonStyleClass: 'p-button-primary p-button-text',
            accept: () => {
                this.changeUserState(newState);
            },
            reject: () => {
                console.log(isActivating ? 'Activation canceled.' : 'Deactivation canceled.');
            },
        };
        this.confirmationService.confirm(confirmation);
    }

    /**
     * Activates or deactivates the selected member. Unlike updateUser() this sends the state alone,
     * so a member whose stored name fails today's validation can still be toggled (#747).
     */
    private changeUserState(newState: UserState): void {
        const user = this.selectedUser;
        if (!user) {
            return;
        }

        const isActivating = newState === UserState.Active;
        const userName = user.firstName + ' ' + user.lastName;
        this.loading = true;

        this.userService
            .updateSubuserState(user.id, newState)
            .pipe(
                tap((response: User) => {
                    // Apply the confirmed state while preserving the other locally displayed fields.
                    const updatedUser = CloneUtils.cloneClassSafe(user);
                    updatedUser.state = response.state ?? newState;
                    this.applyUpdatedUser(updatedUser);

                    this.selectedUser = undefined;
                    this.showAddEditDialog = false;
                    this.toastService.successNoTranslate(
                        this.translate.instant(isActivating ? 'member_activate_header' : 'member_deactivated_header', {
                            userName,
                        }),
                        this.translate.instant(isActivating ? 'member_activate_success' : 'member_deactivated_success'),
                    );
                }),
                catchError((err: HttpErrorResponse) => {
                    this.userUpdateError.show(err, 'member_state_change_error');
                    return of(null);
                }),
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    /** Replaces the user in the unfiltered list and rebuilds the displayed list and group mapping. */
    private applyUpdatedUser(user: User): void {
        const indexOriginal = this.usersOriginal.findIndex((u: User) => u.id === user.id);
        if (indexOriginal !== -1) {
            this.usersOriginal[indexOriginal] = user;
        }

        this.toggleUserStatus();
        this.users.forEach((u: User) => {
            this.userGroupsMap.set(u.id, this.setUserGroup(u));
        });
    }

    toggleUserStatus(): void {
        this.users = this.usersOriginal.filter((user: User) => user.state === this.selectedUserStatus);
    }

    /**
     * Returns the list of groups the user is a member of
     * @param user
     * @returns string[]: The list of groups the user is a member of
     */
    setUserGroup(user: User, groups: GroupForm[] = this.groups): string[] {
        return groups
            .filter((group) => group.members && group.members.some((member) => member.id === user.id))
            .map((group) => group.name);
    }

    getLoadingState(): number {
        let value;
        if (this.loadingService.isLoading()) {
            value = 0;
        } else {
            if (!this.displayEmptyPage) {
                value = 2;
            } else value = 1;
        }
        return value ?? 0;
    }

    openAddUserDialog(): void {
        this.isSelectedUserInactive = false;
        this.newUser = true;

        this.newUserEmail = '';
        this.newUserFirstname = '';
        this.newUserLastname = '';
        this.newUserState = this.userStates[0].value;
        this.selectedGroups = [];

        // pre-select user roles; select admin for the free plan and disable dropdown, because roles are a pro feature
        if (this.isPro) {
            this.newUserRole = this.getRegularUserRole();
        } else {
            this.newUserRole = this.getAdminRole();
        }
        this.showAddEditDialog = true;
    }

    private getAdminRole(): UserPermission[] {
        return this.roles[0].value;
    }

    private getRegularUserRole(): UserPermission[] {
        return this.roles[2].value;
    }

    showUserRolesInfoWindow(): void {
        this.showUserRolesInfo = true;
    }

    goToLicenses(): void {
        void this.router.navigateByUrl('/settings/store');
    }

    giveRoletoPermissionSet(user: User): string {
        // @ts-expect-error -- TODO fix this when building the SDK
        const userObj = User.fromJson(user);
        let returnKey = '';
        try {
            if (userObj.hasMultiPermission(this.roleService.adminRoles)) {
                returnKey = 'Admin';
            } else if (userObj.hasMultiPermission(this.roleService.managerRoles)) {
                returnKey = 'Manager';
            } else if (userObj.hasMultiPermission(this.roleService.userRoles)) {
                this.translate.get('regular_user').subscribe((translation) => {
                    returnKey = translation;
                });
            }
        } catch (e) {
            console.log('Error in giveRoletoPermissionSet: ', e);
        }
        return returnKey;
    }

    onRowSelect(event: TableRowSelectEvent<User>): void {
        let user = Array.isArray(event.data) ? event.data[0] : event.data;
        if (user === undefined) {
            return;
        }

        // Hotfix for #615 - during the update process the user is replaced by an updated version of wrong type (UserForm instead of User)
        // TODO - implement a proper fix for the issue, fix other TODO and rewrite update behavior
        if (!((user as unknown) instanceof User)) {
            user = User.fromJson(user as unknown as UserForm);
        }

        this.selUserIsCurrUser = user.id === this.currentUser?.id;
        this.isSelectedUserInactive = user.state === UserState.Inactive;

        this.newUser = false;

        if (
            this.currentUser &&
            this.currentUser.hasMultiPermission([UserPermission.USER_EDIT, UserPermission.USER_EDIT_PERMISSION])
        ) {
            this.selectedUser = JSON.parse(JSON.stringify(this.selectedUser));
            this.emailChanged = false;

            this.newUserEmail = user.email;
            this.newUserFirstname = user.firstName;
            this.newUserLastname = user.lastName;
            this.newUserState = this.userStates.find((s) => s.value === user.state)?.value;
            this.newUserRole = this.getRolePermissions(user);
            this.originalUserRole = this.newUserRole ? [...this.newUserRole] : undefined;
            // Preselect the groups the user is part of
            this.selectedGroups = this.groups.filter(
                (group) => group.members && group.members.some((member) => member.id === user.id),
            );
            this.showAddEditDialog = true;
        }
    }

    confirmDeleteUser(): void {
        if (!this.selectedUser) return;

        this.selectedUserToDelete = this.selectedUser;
        this.deleteDialogVisible = true;
        // Reset on open, not on close: the dialog's X icon can close it without going through
        // closeDeleteDialog(), so this is the one spot guaranteed to run before every reuse.
        this.clearDeleteConfirmation();
    }

    deleteUser(): void {
        if (!this.selectedUserToDelete) return;

        this.loading = true;

        this.userService
            .deleteUser(this.selectedUserToDelete.id)
            .pipe(
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: () => {
                    this.toastService.success('standard_success', 'user_deleted');

                    this.users = this.users.filter((user) => user.id !== this.selectedUserToDelete!.id);
                    this.usersOriginal = this.usersOriginal.filter(
                        (user: User) => user.id !== this.selectedUserToDelete!.id,
                    );

                    this.deleteDialogVisible = false;
                    this.selectedUserToDelete = null;
                    this.showAddEditDialog = false;
                },
                error: (_error) => {
                    this.toastService.error('standard_error', 'error_deleting_user');
                },
            });
    }

    /**
     * An order agnostic method to match a users permission list to roles(Admin, Manager, User).
     * @param user The user object to check permissions for
     * @returns The matched role permissions or undefined if no match found
     */
    private getRolePermissions(user: User | undefined): UserPermission[] | undefined {
        if (!user) {
            return undefined;
        }

        if (user.hasMultiPermission === undefined) {
            const msg = 'Unexpected error: User object does not have hasMultiPermission() method';
            console.error(msg, user);
            this.toastService.toast(StdToasts.UNEXPECTED_ERROR);
            throw new Error(msg);
        }

        // Check hierarchy: Admin → Manager → User
        if (user.hasMultiPermission(this.roleService.adminRoles)) {
            return this.roleService.adminRoles;
        } else if (user.hasMultiPermission(this.roleService.managerRoles)) {
            return this.roleService.managerRoles;
        } else if (user.hasMultiPermission(this.roleService.userRoles)) {
            return this.roleService.userRoles;
        }

        return undefined;
    }

    private updateUser(): void {
        if (!this.selectedUser) {
            return;
        }
        const changes = this.getMemberChanges();
        const groupsHaveChanged = this.hasGroupChanges();
        this.loading = true;

        const user = CloneUtils.cloneClassSafe(this.selectedUser);
        const isCurrentUser = user.id === this.userService.currentUser?.id;
        const currentEmail = user.email;
        if (changes.email !== undefined && !this.validateEmail(changes.email)) {
            this.loading = false;
            this.toastService.error('standard_error', 'standard_email_validation_error');
            return;
        }

        // Validate edited names; an unchanged legacy name must not block role/group changes.
        const emptyNameProperty = this.getEmptyNameField(changes);
        if (emptyNameProperty) {
            this.loading = false;
            this.userUpdateError.showValidationError('IsValidName', emptyNameProperty);
            return;
        }

        // TODO remove most of this rxjs stuff - translations are not needed and the rest of the code is unnecessary complicated
        this.translate
            .get([
                'standard_error',
                'standard_email_validation_error',
                'standard_success',
                'user_licenseErrorHeader',
                'user_licenseErrorBody',
                'standard_error',
                'standard_error_sorry',
            ])
            .pipe(
                switchMap((_translations) => {
                    const updateUser$ =
                        Object.keys(changes).length === 0 && groupsHaveChanged
                            ? of(user)
                            : isCurrentUser
                              ? this.userService.patchCurrentUser(changes)
                              : this.userService.patchSubuser(user.id, changes);

                    return updateUser$.pipe(
                        switchMap((response: User) => {
                            // Update the user object with the response
                            Object.assign(user, response);

                            let updateGroupAssignments$: Observable<null | GroupForm[]> = of(null);

                            if (groupsHaveChanged) {
                                updateGroupAssignments$ = this.groupService
                                    .updateUserGroupAssignments(
                                        user.id,
                                        this.selectedGroups.map((group) => group.id),
                                    )
                                    .pipe(
                                        tap((allUpdatedGroups: GroupForm[]) => {
                                            this.groups = allUpdatedGroups;
                                            this.userGroupsMap.set(user.id, this.setUserGroup(user, this.groups));
                                        }),
                                    );
                            }

                            return updateGroupAssignments$.pipe(
                                switchMap(() => {
                                    if (currentEmail !== this.newUserEmail) {
                                        return this.userService.requestPasswordReset(user.email).pipe(
                                            tap(() => {
                                                this.emailChanged = false;
                                            }),
                                        );
                                    } else {
                                        return of(null);
                                    }
                                }),
                            );
                        }),
                        tap(() => {
                            if (isCurrentUser) {
                                this.userService.loadCurrentUser(true);
                            }

                            // updates the lists without using this.load
                            this.applyUpdatedUser(user);
                            this.selectedUser = undefined;

                            this.toastService.success('standard_success', 'standard_success');
                            this.loading = false;
                            this.showAddEditDialog = false;
                        }),
                        catchError((err: HttpErrorResponse) => {
                            this.loading = false;
                            this.userUpdateError.show(err, 'standard_error_sorry');
                            return of(null);
                        }),
                    );
                }),
            )
            .subscribe();
    }

    checkEmail(_event: Event): void {
        if (this.selectedUser) {
            if (this.selectedUser.email !== this.newUserEmail) {
                this.emailChanged = true;
            } else {
                this.emailChanged = false;
            }
        }
    }

    addOrEditUser(): void {
        if (this.selectedUser) {
            this.updateUser();
        } else {
            this.addNewUser();
        }
    }

    addNewUser(): void {
        const newUser: SubuserCreateRequestDto = {
            email: this.newUserEmail,
            firstName: this.newUserFirstname,
            lastName: this.newUserLastname,
            settings: {
                language: this.currentUser?.settings?.language ?? 'de',
                color: 'light',
            },
            permissions: this.newUserRole || [],
            templateId: this.currentUser?.templateId ?? 0,
        };

        if (!this.validateEmail(newUser.email)) {
            this.toastService.error('standard_error', 'standard_email_validation_error');
            return;
        }

        this.userService
            .createSubuser(newUser)
            .pipe(
                switchMap((newCreUser) => {
                    if (this.selectedGroups.length > 0) {
                        return this.groupService
                            .updateUserGroupAssignments(
                                newCreUser.id,
                                this.selectedGroups.map((group) => group.id),
                            )
                            .pipe(
                                tap((allUpdatedGroups: GroupForm[]) => {
                                    this.groups = allUpdatedGroups;
                                    this.userGroupsMap.set(newCreUser.id, this.setUserGroup(newCreUser, this.groups));
                                }),
                                switchMap(() => of(newCreUser)),
                            );
                    } else {
                        return of(newCreUser);
                    }
                }),
                tap(() => this.addUserToCRM()),
                tap(() => {
                    this.toastService.success('standard_success', 'user_subusercreated');
                    this.loadingService.setLoading(true);
                    this.users = [];
                }),
                switchMap(() => this.userService.loadAccount(true)),
                tap((account: Account) => {
                    this.licenses = account.licenses;
                    // Rebuild both lists the way ngOnInit does. New subusers are created
                    // Active, so reset the filter to Active to make sure the invited user is visible.
                    this.usersOriginal = account.getUsers();
                    this.selectedUserStatus = UserState.Active;
                    this.toggleUserStatus();
                    this.showAddEditDialog = false;
                    this.loadingService.setLoading(false);
                    this.updateParent.emit(); // Emit the event to notify the parent component
                }),
                catchError((err: HttpErrorResponse) => {
                    // handle validation errors, e.g. bad name
                    this.userUpdateError.show(err, 'user_subusernotcreated');
                    return of(null);
                }),
            )
            .subscribe();
        this.newUser = false;
        this.displayEmptyPage = false;
    }

    addUserToCRM(): void {
        void this.crmWebhookService.send(CrmWebhookEvent.createSubuser, {
            email: this.newUserEmail,
            first_name: this.newUserFirstname,
            last_name: this.newUserLastname,
            language: this.currentUser?.settings?.language ?? 'en',
            cloud_account_id: this.currentUser?.accountId,
        });
    }

    getDialogHeader(): string {
        let textHeaderAdd = '';
        let textHeadersEdit = '';
        let textHeaderCurrEdit = '';

        this.translate.get(['user_add', 'user_edit', 'curr_user_edit']).subscribe((translations) => {
            textHeadersEdit = translations.user_edit;
            textHeaderAdd = translations.user_add;
            textHeaderCurrEdit = translations.curr_user_edit;
        });

        return this.selectedUser === undefined
            ? textHeaderAdd
            : this.selUserIsCurrUser
              ? textHeaderCurrEdit
              : textHeadersEdit;
    }

    getDialogButton(): string {
        let textbuttonAdd = '';
        let textButtonEdit = '';
        this.translate.get(['user_save', 'user_invite']).subscribe((translations) => {
            textButtonEdit = translations.user_save;
            textbuttonAdd = translations.user_invite;
        });

        return this.selectedUser === undefined ? textbuttonAdd : textButtonEdit;
    }

    validateEmail(email: string): boolean {
        const sampleRegEx: RegExp =
            /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
        if (!email) {
            return false;
        } else {
            if (sampleRegEx.test(email)) {
                return true;
            } else {
                return false;
            }
        }
    }

    private hasRoleChanges(): boolean {
        return (
            this.newUserRole !== undefined &&
            !DataUtils.isEqual(
                [...this.newUserRole].sort(),
                [...(this.originalUserRole ?? this.selectedUser?.permissions ?? [])].sort(),
            )
        );
    }

    private hasGroupChanges(): boolean {
        const originalIds = this.groups
            .filter((group) => group.members?.some((member) => member.id === this.selectedUser?.id))
            .map((group) => group.id)
            .sort();
        return !DataUtils.isEqual(this.selectedGroups.map((group) => group.id).sort(), originalIds);
    }

    /** Builds a request containing only fields changed in the member dialog. */
    private getMemberChanges(): UserUpdateForm {
        const user = this.selectedUser;
        const changes: UserUpdateForm = {};
        if (this.newUserEmail !== user?.email) changes.email = this.newUserEmail;
        if (this.newUserFirstname !== user?.firstName) changes.firstName = this.newUserFirstname;
        if (this.newUserLastname !== user?.lastName) changes.lastName = this.newUserLastname;
        if (this.hasRoleChanges()) changes.permissions = this.newUserRole;
        return changes;
    }

    private getEmptyNameField(changes: UserUpdateForm): MemberNameField | undefined {
        if (changes.firstName?.trim().length === 0) return 'firstName';
        if (changes.lastName?.trim().length === 0) return 'lastName';
        return undefined;
    }

    /** True when the member dialog has edits that would be lost by an action other than saving. */
    hasUnsavedChanges(): boolean {
        return Object.keys(this.getMemberChanges()).length > 0 || this.hasGroupChanges();
    }

    isSaveButtonDisabled(): boolean {
        const changes = this.getMemberChanges();
        return !!this.getEmptyNameField(changes) || (changes.email !== undefined && !this.validateEmail(changes.email));
    }

    /** True when the primary dialog button activates the member instead of saving the form. */
    isActivateAction(): boolean {
        return this.selectedUserStatus === UserState.Inactive && !this.newUser;
    }

    isPrimaryDialogButtonDisabled(): boolean {
        return this.isActivateAction() ? false : this.isSaveButtonDisabled();
    }
}
